import React, { useState, useEffect } from 'react';
import {
  getConnections,
  addConnection,
  updateConnection,
  deleteConnection,
  reconnectConnection,
  disconnectDevice,
} from '../api';
import { useAuth } from '../context/AuthContext';

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingConnection, setEditingConnection] = useState(null);
  const [newPhone, setNewPhone] = useState('');
  const [newName, setNewName] = useState('');
  const [deviceLimit, setDeviceLimit] = useState(1);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.device_count) {
      setDeviceLimit(user.device_count);
    } else {
      refreshUser().then(() => {
        const stored = localStorage.getItem('user');
        if (stored) {
          const u = JSON.parse(stored);
          if (u.device_count) setDeviceLimit(u.device_count);
        }
      });
    }
  }, [user, refreshUser]);

  useEffect(() => {
    fetchConnections();
    const interval = setInterval(fetchConnections, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchConnections = async () => {
    setLoading(true);
    try {
      const res = await getConnections();
      if (res.status === 1) {
        setConnections(res.data || []);
        setError(null);
      } else {
        setError(res.description || 'Failed to load connections');
      }
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.description || 'Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDevice = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addConnection(newPhone, newName);
      setShowAddModal(false);
      setNewPhone('');
      setNewName('');
      await fetchConnections();
    } catch (e) {
      alert(e.response?.data?.description || 'Failed to add device');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateConnection(editingConnection.id, {
        name: editingConnection.name,
        phoneNumber: editingConnection.phone_number,
      });
      setShowEditModal(false);
      await fetchConnections();
    } catch (e) {
      alert('Update failed: ' + (e.response?.data?.description || e.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (connectionId) => {
    if (!window.confirm('Delete this device?')) return;
    setLoading(true);
    try {
      await deleteConnection(connectionId);
      await fetchConnections();
    } catch (e) {
      alert('Delete failed: ' + (e.response?.data?.description || e.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async (connectionId) => {
    if (!window.confirm('Disconnect this device? (Requires re-scanning QR code to reconnect)')) return;
    setLoading(true);
    try {
      await disconnectDevice(connectionId);
      await fetchConnections();
    } catch (e) {
      alert(e.response?.data?.description || 'Disconnect failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReconnect = async (connectionId) => {
    setLoading(true);
    try {
      await reconnectConnection(connectionId);
      setTimeout(fetchConnections, 2000);
    } catch (e) {
      alert('Reconnect failed: ' + (e.response?.data?.description || e.message));
    } finally {
      setLoading(false);
    }
  };

  // Copy Device Token to Clipboard
  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => alert('Device Token copied!'));
    } else {
      alert('Clipboard API not supported');
    }
  };

  // Status Color Classes
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'connected': return 'bg-green-100 text-green-700';
      case 'connecting':
      case 'qr_pending': return 'bg-yellow-100 text-yellow-700';
      case 'disconnected':
      case 'logged_out': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="w-full h-full flex flex-col p-6 bg-gray-50">
      
      {/* ✅ Added Top Text: Added Device counter */}
      <div className="flex justify-between items-center mb-4 px-1">
        <h2 className="text-gray-800 font-medium text-lg">
          Added Device : {connections.length} out of {deviceLimit}
        </h2>
        {/* Optional: You can add a search bar here in the future */}
      </div>

      {/* White Container */}
      <div className="w-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        
        {/* 🟢 Green Header Row */}
        <div className="flex justify-between items-center px-6 py-4 bg-[#0c5e24] text-white">
          <div className="flex-1 grid grid-cols-5 gap-4 text-sm font-semibold uppercase tracking-wide">
            <div>User</div>
            <div>Device Token</div>
            <div>Status</div>
            <div>Connection Time</div>
            <div className="text-right">Actions</div>
          </div>
          <button
            className="bg-white text-[#0c5e24] px-5 py-1.5 rounded text-sm font-medium hover:bg-gray-100 transition-colors shadow-sm ml-4"
            onClick={() => setShowAddModal(true)}
            disabled={connections.length >= deviceLimit}
          >
            {connections.length >= deviceLimit ? 'Limit Reached' : 'Request to add device'}
          </button>
        </div>

        {/* Table Body */}
        <div className="divide-y divide-gray-100">
          {loading && connections.length === 0 && (
            <div className="p-8 text-center text-gray-400">Loading devices...</div>
          )}

          {connections.map((conn) => (
            <React.Fragment key={conn.id}>
              <div className="px-6 py-4 hover:bg-gray-50 transition-colors flex items-center justify-between gap-4">
                
                {/* 1. User Column */}
                <div className="flex items-center gap-3 w-1/5">
                  <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 flex-shrink-0">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                    </svg>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900">{conn.name || '—'}</span>
                    <span className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.038-1.124.407l-1.014 1.684a8.75 8.75 0 01-5.277-5.277l1.684-1.014c.369-.222.517-.684.407-1.124l-1.106-4.423c-.125-.501-.575-.852-1.091-.852H4.5A2.25 2.25 0 002.25 6.75z" />
                      </svg>
                      {conn.phone_number || '—'}
                    </span>
                  </div>
                </div>

                {/* 2. Device Token Column */}
                <div className="w-1/5 flex items-center gap-2">
                  <div className="font-mono text-xs text-gray-600 bg-gray-50 px-2.5 py-1.5 rounded border border-gray-200 w-full flex justify-between items-center truncate">
                    <span className="truncate mr-2">{conn.device_token || 'N/A'}</span>
                    {conn.device_token && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(conn.device_token); }}
                        className="text-gray-400 hover:text-green-700 transition-colors flex-shrink-0"
                        title="Copy Token"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* 3. Status Column */}
                <div className="w-1/6">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(conn.status)}`}>
                    {conn.status}
                  </span>
                </div>

                {/* 4. Connection Time Column */}
                <div className="w-1/6 text-xs text-gray-600">
                  {conn.token_created_at ? (
                    <div className="flex flex-col">
                      <span>{new Date(conn.token_created_at).toLocaleDateString()}</span>
                      <span className="text-gray-400">{new Date(conn.token_created_at).toLocaleTimeString()}</span>
                    </div>
                  ) : '—'}
                </div>

                {/* 5. Actions Column */}
                <div className="w-1/5 flex justify-end items-center gap-2">
                  
                  {/* ✅ Added: Disconnect Button */}
                  <button
                    className="bg-red-100 text-red-700 hover:bg-red-200 text-xs font-medium px-3 py-1.5 rounded transition-colors"
                    onClick={() => handleDisconnect(conn.id)}
                  >
                    Disconnect
                  </button>

                  <button
                    className="bg-[#0c5e24] hover:bg-green-800 text-white text-xs font-medium px-4 py-1.5 rounded transition-colors"
                    onClick={() => handleReconnect(conn.id)}
                  >
                    Reconnect
                  </button>
                  <button
                    className="p-1.5 text-gray-400 hover:text-green-700 rounded-full hover:bg-green-50 transition-colors"
                    onClick={() => {
                      setEditingConnection(conn);
                      setShowEditModal(true);
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" /></svg>
                  </button>
                  <button
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50 transition-colors"
                    onClick={() => handleDelete(conn.id)}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                  </button>
                </div>
              </div>

              {/* QR Code / Pairing Expander */}
              {conn.status === 'qr_pending' && conn.qr && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 col-span-5">
                  <div className="flex items-center gap-6">
                    <p className="text-sm text-gray-600">Scan this QR code with WhatsApp on your phone:</p>
                    <img src={conn.qr} alt="QR" className="w-24 h-24 border border-gray-200 rounded" />
                  </div>
                </div>
              )}
              {conn.status === 'connecting' && conn.pairing && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 col-span-5">
                  <p className="text-sm text-gray-600">
                    WhatsApp → Settings → Linked Devices → Link a Device → "Link with phone number instead"
                  </p>
                  <p className="font-mono text-lg font-bold text-[#0c5e24] mt-1">{conn.pairing}</p>
                </div>
              )}
            </React.Fragment>
          ))}

          {connections.length === 0 && !loading && (
            <div className="p-8 text-center text-gray-500">
              No devices connected. Click the "Request to add device" button above!
            </div>
          )}
        </div>
      </div>

      {/* ── Add Device Modal ── */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-gray-800">Add WhatsApp Device</h3>
              <button className="text-gray-400 hover:text-gray-600" onClick={() => setShowAddModal(false)}>✕</button>
            </div>
            <form onSubmit={handleAddDevice}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Device Name</label>
                  <input type="text" placeholder="e.g. Office, Personal" value={newName} onChange={(e) => setNewName(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input type="text" placeholder="e.g. 919876543210" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none" />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-[#0c5e24] rounded-lg hover:bg-green-800 transition-colors disabled:opacity-50">Add Device</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit Device Modal ── */}
      {showEditModal && editingConnection && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4" onClick={() => setShowEditModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-gray-800">Edit Device</h3>
              <button className="text-gray-400 hover:text-gray-600" onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Device Name</label>
                  <input type="text" value={editingConnection.name || ''} onChange={(e) => setEditingConnection({ ...editingConnection, name: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input type="text" value={editingConnection.phone_number || ''} onChange={(e) => setEditingConnection({ ...editingConnection, phone_number: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-600 focus:border-transparent outline-none" />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="submit" disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-[#0c5e24] rounded-lg hover:bg-green-800 transition-colors disabled:opacity-50">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}